/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA 02110-1301, USA.
 *
 * Copyright (c) 2023 (original work) Open Assessment Technologies SA;
 */

import { get, writable } from 'svelte/store';
import {
    getEntities,
    getEntity,
    getEntityTableData,
    getEntityData,
    getView,
    getViews,
    createView,
    deleteView
} from '../services/explorerService';
import { __ } from '@oat-sa-private/ui-core';
import { flattenKeys } from '@/core/utils/object/object';
import { showNotification } from '@oat-sa-private/ui-components';
import { mapFilters, mapFields, mapToFilters, mapToFields, mapFormats } from '../helpers';
import { getLocalTimezone } from '@/core/utils/date.js';
import * as localStorageHelper from '@/helpers/localStorageHelper.js';
import { log } from '@/core/utils';

const DONT_ASK_VIEWS_CONFIRMATION_KEY = 'dataExplorerDontAskForViewsConfirmation';
const DONT_ASK_PRIVACY_CONFIRMATION_KEY = 'dataExplorerDontAskForPrivacyConfirmation';
const DONT_ASK_CONFIRMATION_KEY = 'dataExplorerDontAskForConfirmation';
const PAGINATION_LIMIT = 1000;

export const DATE_HISTOGRAM_OPT = 'dateHistogram';

function createStore() {
    const defaults = {
        activeTab: 'filters',
        isNew: true,
        count: false,
        view: {
            id: '',
            name: '',
            isEnabled: false,
            isPrivate: false,
            options: []
        },
        entityId: '',
        entities: {
            list: []
        },
        entityDetails: [],
        entityTableData: {
            reportWidgets: []
        },
        filters: {},
        aggregations: [],
        accumulate: [],
        filterFieldNames: {},
        displayedFieldNames: {},
        subFieldNames: {},
        availableViews: [],
        loading: false,
        timezone: getLocalTimezone(),
        dontAskConfirmation: localStorage.getItem(DONT_ASK_CONFIRMATION_KEY),
        dontAskViewsConfirmation: localStorageHelper.getItem(DONT_ASK_VIEWS_CONFIRMATION_KEY),
        dontAskPrivacyConfirmation: localStorageHelper.getItem(DONT_ASK_PRIVACY_CONFIRMATION_KEY)
    };

    const store = writable(defaults);
    const { set, update, subscribe } = store;

    return {
        subscribe,
        set,
        update,
        get() {
            return get(store);
        },
        reset() {
            set(defaults);
        },
        resetWithValues(applyValues) {
            update(s => ({
                ...s,
                count: false,
                entityId: '',
                accumulate: [],
                filterFieldNames: {},
                filters: {},
                subFieldNames: {},
                displayedFieldNames: {},
                aggregations: [],
                entityDetails: [],
                ...applyValues(s)
            }));
        },
        async loadEntities() {
            const entities = await getEntities();

            update(s => ({
                ...s,
                entities: {
                    list: entities?.entityList.map(({ name }) => name) || []
                }
            }));
        },
        async loadViews() {
            const views = await getViews();

            update(s => ({
                ...s,
                view: {
                    ...s.view,
                    options: views.map(({ name, _id }) => ({ name, id: _id }))
                }
            }));
            return views;
        },
        async loadView(id, viewMode = false) {
            this.resetWithValues(s => ({
                view: {
                    ...s.view,
                    id: '',
                    name: '',
                    isEnabled: viewMode,
                    isPrivate: false
                }
            }));
            const view = await getView(id);
            await this.loadEntities();
            await this.loadEntityDetails(view.entityId);
            const { filters, filterFieldNames } = mapToFilters(view.filters);
            const fields = mapToFields(view.fields);
            update(s => ({
                ...s,
                entityId: view.entityId,
                count: view.count,
                filters: {
                    ...s.filters,
                    ...filters
                },
                displayedFieldNames: fields,
                filterFieldNames,
                aggregations: view.aggregations || [],
                accumulate: view.accumulate || [],
                view: {
                    ...s.view,
                    id: view._id,
                    name: view.name
                }
            }));
        },
        async saveView() {
            const {
                entityId,
                entityDetails,
                view,
                count,
                displayedFieldNames,
                filters: filtersStore,
                aggregations,
                accumulate,
                subFieldNames
            } = get(store);
            const filters = mapFilters(entityDetails, filtersStore);
            const fields = mapFields(displayedFieldNames, subFieldNames);
            const body = {
                name: view.name,
                entityId,
                count,
                fields,
                filters,
                aggregations,
                accumulate,
                isPrivate: view.isPrivate
            };
            const result = await createView({ body });
            update(s => ({
                ...s,
                view: {
                    ...s.view,
                    id: result.id,
                    options: [
                        ...s.view.options.filter(item => result.id !== item.id),
                        { name: result.name, id: result.id }
                    ]
                }
            }));
        },
        async loadEntityDetails(entityId) {
            const entityDetails = await getEntity(entityId);
            const details = entityDetails?.entityDescription || [];
            const filters = {};
            const filterFieldNames = {};
            details.forEach(detail => {
                filters[detail.fieldName] = { value: '', from: '', to: '' };
                filterFieldNames[detail.fieldName] = false;
            });
            update(s => ({
                ...s,
                entityDetails: details,
                filters,
                filterFieldNames
            }));
        },
        async loadEntityTableData() {
            const {
                entityId,
                count,
                displayedFieldNames,
                entityDetails,
                filters,
                aggregations: aggregationsStore,
                accumulate,
                subFieldNames
            } = get(store);
            const aggregations = [...aggregationsStore];
            const fields = mapFields(displayedFieldNames, subFieldNames);
            const body = {
                fields,
                filters: mapFilters(entityDetails, filters),
                aggregations,
                accumulate,
                count: !!count,
                widgets: ['table', ...(count ? ['frame'] : [])]
            };

            try {
                const entityTableData = await getEntityTableData({ entityId, body });
                update(s => ({
                    ...s,
                    entityTableData: {
                        ...entityTableData.tableConfig,
                        reportWidgets: entityTableData.tableConfig.reportWidgets.map(widget => ({
                            // we create an instanceId field to serve as svelte key in a `each` tag in ReportWidgetsGrid.svelte.
                            // The instanceId field is necessary in the case of customReports.store.js
                            instanceId: widget.widgetId,
                            ...flattenKeys(widget, ['content'])
                        }))
                    }
                }));
            } catch (e) {
                log.error(`Error getting entity table data`, e);

                showNotification({
                    title: __('Error getting data'),
                    message:
                        e.message &&
                        __('There were an error with the filters you defined for this request. Please try again'),
                    hierarchy: 'alert'
                });
            }
        },
        async searchEntityData(id, loaderParameters) {
            const {
                entityId,
                displayedFieldNames,
                subFieldNames,
                filters: filtersStore,
                entityDetails,
                aggregations,
                accumulate
            } = get(store);
            const filters = mapFilters(entityDetails, filtersStore);
            const fields = mapFields(displayedFieldNames, subFieldNames);
            const format = mapFormats(entityDetails, displayedFieldNames);
            const body = {
                queryParams: {
                    pageSize: loaderParameters?.pageSize || 10,
                    pageNumber: loaderParameters?.pageNumber || 0,
                    fields,
                    filters,
                    format,
                    aggregations,
                    accumulate
                }
            };
            const searchData = await getEntityData({ entityId: id || entityId, body });

            if (searchData.metadata?.pagination?.pageCount > PAGINATION_LIMIT) {
                searchData.metadata.pagination.pageCount = PAGINATION_LIMIT;
            }

            return searchData;
        },
        async getEntityQueryParams(id, loaderParameters) {
            const {
                entityId,
                displayedFieldNames,
                subFieldNames,
                filters: filtersStore,
                entityDetails,
                aggregations,
                accumulate
            } = get(store);
            const filters = mapFilters(entityDetails, filtersStore);
            const fields = mapFields(displayedFieldNames, subFieldNames);
            const format = mapFormats(entityDetails, displayedFieldNames);
            const body = {
                pageSize: loaderParameters?.pageSize || 10,
                pageNumber: loaderParameters?.pageNumber || 0,
                fields,
                filters,
                format,
                aggregations,
                accumulate
            };
            return { entityId: id || entityId, queryParams: body };
        },
        clearFilter(fieldName) {
            update(s => ({
                ...s,
                filters: {
                    ...s.filters,
                    [fieldName]: { value: '', from: '', to: '' }
                },
                filterFieldNames: {
                    ...s.filterFieldNames,
                    [fieldName]: false
                }
            }));
        },
        /**
         * Update display fields
         * @param {object} param0
         * @param {object} param0.newDisplayedFieldNames new values for displayedFieldNames
         * @param {string} param0.removeSubFieldName a subfield name to remove (typically used when a displayedFieldName has been deselected)
         */
        updateDisplayFields({ newDisplayedFieldNames, removeSubFieldName }) {
            update(s => {
                // Clearing aggregations fields if they aren't selected to display
                const aggregations = s.aggregations.filter(
                    item => item.type !== 'dateHistogram' || newDisplayedFieldNames[item.field]
                );

                return {
                    ...s,
                    aggregations,
                    displayedFieldNames: newDisplayedFieldNames,
                    ...(removeSubFieldName && {
                        subFieldNames: { ...s.subFieldNames, [removeSubFieldName]: '' }
                    })
                };
            });
        },
        setLoading(value) {
            update(data => ({ ...data, loading: value }));
        },
        setViewConfirmationModalsVissibility(hidden) {
            localStorageHelper.setItem(DONT_ASK_VIEWS_CONFIRMATION_KEY, hidden);
            update(data => ({
                ...data,
                dontAskViewsConfirmation: localStorageHelper.getItem(DONT_ASK_VIEWS_CONFIRMATION_KEY)
            }));
        },
        setPrivacyConfirmationModalsVissibility(hidden) {
            localStorageHelper.setItem(DONT_ASK_PRIVACY_CONFIRMATION_KEY, hidden);
            update(data => ({
                ...data,
                dontAskPrivacyConfirmation: localStorageHelper.getItem(DONT_ASK_PRIVACY_CONFIRMATION_KEY)
            }));
        },
        resetSelectedView() {
            this.resetWithValues(s => ({
                view: {
                    ...s.view,
                    id: '',
                    name: '',
                    isPrivate: false
                }
            }));
        },
        async deleteViews(views) {
            try {
                await Promise.all(views.map(view => deleteView(view)));
            } catch (e) {
                log.error(`Error deleting configurations`, e);
            } finally {
                update(s => {
                    const options = s.view.options.filter(item => !views.includes(item.id));
                    return {
                        ...s,
                        view: {
                            ...s.view,
                            options
                        }
                    };
                });
            }
        },
        hideFutureConfirmationModals() {
            localStorage.setItem(DONT_ASK_CONFIRMATION_KEY, true);
            update(data => ({
                ...data,
                dontAskConfirmation: localStorage.getItem(DONT_ASK_CONFIRMATION_KEY)
            }));
        }
    };
}

export default createStore();
